import EventEmitter from 'events';
import { Disposable } from 'vscode-languageserver';
import { log } from '../log';
import {
  CIRCUIT_BREAK_INTERVAL_MS,
  CircuitBreaker,
  CircuitBreakerState,
  MAX_ERRORS_BEFORE_CIRCUIT_BREAK,
} from './circuit_breaker';

export class FixedTimeCircuitBreaker implements CircuitBreaker {
  #name: string;

  #state: CircuitBreakerState = CircuitBreakerState.CLOSED;

  readonly #maxErrorsBeforeBreaking: number;

  readonly #breakTimeMs: number;

  #errorCount = 0;

  #eventEmitter = new EventEmitter();

  #timeoutId: NodeJS.Timeout | null = null;

  /**
   * @param name identifier of the circuit breaker that will appear in the logs
   * */
  constructor(
    name: string,
    maxErrorsBeforeBreaking: number = MAX_ERRORS_BEFORE_CIRCUIT_BREAK,
    breakTimeMs: number = CIRCUIT_BREAK_INTERVAL_MS,
  ) {
    this.#name = name;
    this.#maxErrorsBeforeBreaking = maxErrorsBeforeBreaking;
    this.#breakTimeMs = breakTimeMs;
  }

  error() {
    this.#errorCount += 1;
    if (this.#errorCount >= this.#maxErrorsBeforeBreaking) {
      this.#open();
    }
  }

  #open() {
    this.#state = CircuitBreakerState.OPEN;
    log.warn(
      `Excess errors detected, opening '${this.#name}' circuit breaker for ${this.#breakTimeMs / 1000} second(s).`,
    );
    this.#timeoutId = setTimeout(() => this.#close(), this.#breakTimeMs);
    this.#eventEmitter.emit('open');
  }

  isOpen() {
    return this.#state === CircuitBreakerState.OPEN;
  }

  #close() {
    if (this.#state === CircuitBreakerState.CLOSED) {
      return;
    }
    if (this.#timeoutId) {
      clearTimeout(this.#timeoutId);
    }
    this.#state = CircuitBreakerState.CLOSED;
    this.#eventEmitter.emit('close');
  }

  success() {
    this.#errorCount = 0;
    this.#close();
  }

  onOpen(listener: () => void): Disposable {
    this.#eventEmitter.on('open', listener);
    return { dispose: () => this.#eventEmitter.removeListener('open', listener) };
  }

  onClose(listener: () => void): Disposable {
    this.#eventEmitter.on('close', listener);
    return { dispose: () => this.#eventEmitter.removeListener('close', listener) };
  }
}
